import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { formatDate, formatTime } from '../lib/format'
import { accessesApi, type Access, type PortalMe } from './api'

const KIND_LABEL: Record<string, string> = {
  cloud: 'Облако',
  database: 'Доступ в базу',
  portal: 'Личный кабинет',
}

/**
 * Доступы организации в её кабинете.
 *
 * Список приходит без паролей — они запрашиваются по одной записи, отдельным
 * нажатием. Так утечка одного ответа не становится утечкой всех доступов сразу,
 * и каждый показ попадает в журнал с именем и временем.
 */
export default function PortalAccesses({ me }: { me: PortalMe }) {
  const [openLog, setOpenLog] = useState(false)

  const list = useQuery({ queryKey: ['portal', 'accesses'], queryFn: accessesApi.list })

  if (!me.can_view_accesses) {
    return (
      <>
        <h1 className="page-title">Доступы</h1>
        <div className="warn">
          Доступы вам не открыты. Попросите ответственного за кабинет в вашей
          организации разрешить их просмотр — это делается в разделе «Сотрудники».
        </div>
      </>
    )
  }

  const rows = list.data ?? []

  return (
    <>
      <h1 className="page-title">Доступы</h1>

      <p className="hint">
        Это учётные данные к вашим системам, которые мы ведём. Пароль показывается
        по одному нажатию и записывается в журнал: видно, кто и когда смотрел.
      </p>

      {me.is_admin && (
        <div className="toolbar">
          <button className="btn" onClick={() => setOpenLog(true)}>
            Кто смотрел пароли
          </button>
        </div>
      )}

      {list.isLoading && <p className="empty">Загрузка…</p>}
      {!list.isLoading && rows.length === 0 && (
        <p className="empty">Доступов пока не заведено</p>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {rows.map((a) => (
          <AccessCard key={a.id} access={a} />
        ))}
      </div>

      {openLog && <ViewLog onClose={() => setOpenLog(false)} />}
    </>
  )
}

function AccessCard({ access }: { access: Access }) {
  const [opened, setOpened] = useState<Access | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const shown = opened ?? access

  async function reveal() {
    setBusy(true)
    setError('')
    try {
      setOpened(await accessesApi.reveal(access.id))
    } catch (e: any) {
      setError(e?.message ?? 'Не удалось показать')
    } finally {
      setBusy(false)
    }
  }

  const hasSecrets =
    !!access.login || !!access.server_address || access.rows.length > 0

  return (
    <div className="pv-card">
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
        <div className="tk-subject">{shown.title}</div>
        <span className="tk-chip tk-resolved">{KIND_LABEL[shown.kind] ?? shown.kind}</span>
      </div>

      {error && <div className="error" style={{ marginTop: 8 }}>{error}</div>}

      <div style={{ marginTop: 10 }}>
        {shown.product_name && <Row k="Продукт" v={shown.product_name} />}
        {shown.service_name && <Row k="Сервис" v={shown.service_name} />}
        {shown.server_address && <Row k="Адрес" v={shown.server_address} mono />}
        {shown.login && <Row k="Логин" v={shown.login} mono />}
        {shown.password && <Row k="Пароль" v={shown.password} mono secret />}
        {shown.recovery_phone && <Row k="Телефон восстановления" v={shown.recovery_phone} />}
        {shown.recovery_email && <Row k="Почта восстановления" v={shown.recovery_email} />}
      </div>

      {shown.rows.length > 0 && (
        <div style={{ marginTop: 10, borderTop: '1px solid var(--line)', paddingTop: 10 }}>
          {shown.rows.map((r, i) => (
            <div key={r.id ?? i} style={{ marginBottom: 8 }}>
              {r.computer && <div style={{ fontWeight: 700 }}>{r.computer}</div>}
              {r.product_name && <div className="cell-sub">{r.product_name}</div>}
              {r.url && <Row k="Ссылка" v={r.url} mono />}
              {r.user_name && <Row k="Пользователь" v={r.user_name} />}
              {r.login && <Row k="Логин" v={r.login} mono />}
              {r.password && <Row k="Пароль" v={r.password} mono secret />}
            </div>
          ))}
        </div>
      )}

      {shown.note && <p className="cell-sub" style={{ whiteSpace: 'pre-wrap' }}>{shown.note}</p>}

      {!opened && hasSecrets && (
        <button className="btn" style={{ marginTop: 10 }} disabled={busy} onClick={() => void reveal()}>
          {busy ? 'Открываем…' : 'Показать пароли'}
        </button>
      )}
      {opened && (
        <p className="hint-inline" style={{ display: 'block', marginTop: 10 }}>
          Просмотр записан. Закройте страницу, когда закончите.
        </p>
      )}
    </div>
  )
}

function Row({
  k,
  v,
  mono,
  secret,
}: {
  k: string
  v: string
  mono?: boolean
  secret?: boolean
}) {
  const [copied, setCopied] = useState(false)

  async function copy() {
    try {
      await navigator.clipboard.writeText(v)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1500)
    } catch {
      /* браузер не дал — человек выделит вручную */
    }
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '3px 0' }}>
      <span className="cell-sub" style={{ minWidth: 170 }}>{k}</span>
      <span
        className={mono ? 'mono' : undefined}
        style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere', fontWeight: secret ? 700 : 400 }}
      >
        {v}
      </span>
      <button className="btn small" onClick={() => void copy()}>
        {copied ? '✓' : 'Копировать'}
      </button>
    </div>
  )
}

function ViewLog({ onClose }: { onClose: () => void }) {
  const log = useQuery({ queryKey: ['portal', 'access-log'], queryFn: accessesApi.log })

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 620 }}>
        <h2>Кто смотрел пароли</h2>
        <div className="modal-body">
          <p className="hint">
            Последние двести просмотров. Если кто-то смотрел доступ, которым не
            занимается, — повод спросить.
          </p>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Когда</th>
                  <th>Кто</th>
                  <th>Что смотрел</th>
                </tr>
              </thead>
              <tbody>
                {log.isLoading && (
                  <tr>
                    <td colSpan={3} className="empty">Загрузка…</td>
                  </tr>
                )}
                {!log.isLoading && (log.data ?? []).length === 0 && (
                  <tr>
                    <td colSpan={3} className="empty">Паролей ещё никто не смотрел</td>
                  </tr>
                )}
                {log.data?.map((e) => (
                  <tr key={e.id}>
                    <td className="nowrap">
                      {formatDate(e.created_at)} {formatTime(e.created_at)}
                    </td>
                    <td>{e.viewer_name}</td>
                    <td>{e.access_title}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="modal-actions">
          <button className="btn" onClick={onClose}>Закрыть</button>
        </div>
      </div>
    </div>
  )
}
